import { Autocomplete, Checkbox, Divider, FormControlLabel, InputAdornment, inputClasses, Stack } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import { BILLING_INTERVALS, COMMITMENT_TYPES } from '@weft/types';
import { isNotNullOrUndefined, isNullOrUndefined, values } from '@weft/utils';
import { get } from 'lodash';
import React, { useEffect } from 'react';
import type { FieldPath } from 'react-hook-form';
import { Controller, useFormContext } from 'react-hook-form';
import { VayuTextField } from '../../../../../../../components/design/vayu-theme-components/VayuTextField';
import { useEffectOnce } from '../../../../../../../hooks/use-effect-once';
import { getCurrencySymbol } from '../../../../../../../services/currency-symbol-mapper';
import { useChargeContext } from '../../../charge-context';
import type { PlanInputForm } from '../../../CreatePlanForm/create-plan-form-types/plan-input-form';
import { ChargeEditDrawerContentItemOptional } from '../../../FixedChargeEditDrawer/fixed-charge-edit-drawer-components/ChargeEditDrawerContentItemOptional';
import { getBillingIntervalOptionLabel } from './TiersChargeEditDrawerContent';

export const PerUnitChargeEditDrawerContent: React.FC = () => {
  const { itemIndex: productIndex, currency } = useChargeContext();
  const { watch, register, formState, control, setValue } = useFormContext<PlanInputForm>();
  const { errors } = formState;

  const registerDescription = register(`usageProducts.${productIndex}.description`);
  const registerChunkSize = register(`usageProducts.${productIndex}.variants.0.pricing.priceConfiguration.chunkSize`);
  const registerCommitmentType = register(`usageProducts.${productIndex}.commitment.type`);
  const registerBaseAmount = register(`usageProducts.${productIndex}.basePrice`);
  const registerCommitment = register(`usageProducts.${productIndex}.commitment.amount`, {
    valueAsNumber: true,
  });

  const commitment = watch(`usageProducts.${productIndex}.commitment`);
  const commitmentAmount = watch(`usageProducts.${productIndex}.commitment.amount`);
  const commitmentType = watch(`usageProducts.${productIndex}.commitment.type`);

  useEffectOnce(() => {
    if (isNullOrUndefined(watch(`usageProducts.${productIndex}.commitment`))) {
      setValue(`usageProducts.${productIndex}.commitment`, undefined);
    }
  });

  useEffect(() => {
    if (isNotNullOrUndefined(commitment) && isNullOrUndefined(commitmentAmount) && isNullOrUndefined(commitmentType)) {
      setValue(`usageProducts.${productIndex}.commitment`, undefined);
    }
  }, [commitment, commitmentAmount, commitmentType, productIndex, setValue]);

  const handleCommitmentAmountChange = (value: number) => {
    const newCommitment = {
      amount: value,
      type: COMMITMENT_TYPES.Units,
    };

    setValue(`usageProducts.${productIndex}.commitment`, value >= 0 ? newCommitment : undefined);
  };

  const handleNumberChange = (field: FieldPath<PlanInputForm>) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = Number(event.target.value);

      setValue(field, value >= 0 ? value : 0);
    };
  };

  return (
    <Stack
      className="flex flex-col gap-y-6"
      divider={<Divider/>}
    >
      <ChargeEditDrawerContentItemOptional
        fieldPath={registerDescription.name}
        label="Add description"
        hint="Product description that will show on the invoice."
      >
        <VayuTextField
          {...registerDescription}
          fullWidth
          focused
          placeholder="Add a description"
          multiline
          error={isNotNullOrUndefined(get(errors, registerDescription.name))}
        />
      </ChargeEditDrawerContentItemOptional>

      <Controller
        control={control}
        name={registerChunkSize.name}
        render={({ field }) => (
          <VayuTextField
            {...field}
            focused
            label="Bulk size"
            placeholder="Chunk size"
            error={isNotNullOrUndefined(get(errors, registerChunkSize.name))}
            type="number"
            onChange={handleNumberChange(registerChunkSize.name)}
          />
        )}
      />

      <Controller
        control={control}
        name={`usageProducts.${productIndex}.billingInterval`}
        render={({ field }) => (
          <Autocomplete
            autoHighlight
            size="small"
            getOptionLabel={getBillingIntervalOptionLabel}
            options={values(BILLING_INTERVALS)}
            renderInput={(params) => (
              <VayuTextField
                {...params}
                {...field}
                focused
                hint="Charges will accumulate over the selected period according to the pricing tier and reset at the start of the next one."
                label="Unit accumulation"
                inputProps={{
                  ...params.inputProps,
                  autoComplete: 'new-password',
                }}
              />
            )}
            value={field.value ?? ''}
            onChange={(event, newValue) => {
              event.preventDefault();
              field.onChange?.(newValue);
            }}
          />
        )}
      />

      <ChargeEditDrawerContentItemOptional
        fieldPath={registerCommitment.name}
        hint={`A set amount of included units or ${getCurrencySymbol(currency)} credits. Once the set commitment value is reached, you can charge for any additional overage.`}
        label="Add commitment"
        handleOnChecked={(checked) => {
          if (checked) {
            setValue(`usageProducts.${productIndex}.commitment`, {
              amount: 0,
              type: 'Units',
            });
          } else {
            setValue(`usageProducts.${productIndex}.commitment`, undefined);
            setValue(`usageProducts.${productIndex}.isPeriodicConsumption`, undefined);
          }
        }}
      >
        <div className="flex flex-col gap-y-4">
          <div className="flex">
            <VayuTextField
              {...registerCommitment}
              focused
              placeholder="Amount"
              error={isNotNullOrUndefined(get(errors, registerCommitment.name))}
              onChange={(event) => handleCommitmentAmountChange(Number(event.target.value))}
              type="number"
              sx={{
                [`& .${inputClasses.root}`]: {
                  '&.Mui-focused': {
                    borderRadius: '10px 0 0 10px !important',
                  },
                },
              }}
              InputProps={{
                startAdornment: watch(registerCommitmentType.name) === COMMITMENT_TYPES.Price && (
                  <InputAdornment position="end">
                    {`${getCurrencySymbol(currency)} `}
                  </InputAdornment>
                ),
              }}
            />

            <Controller
              control={control}
              name={registerCommitmentType.name}
              render={({ field }) => (
                <VayuTextField
                  {...field}
                  select
                  focused
                  defaultValue={COMMITMENT_TYPES.Units}
                  sx={{
                    width: 140,
                    [`& .${inputClasses.root}`]: {
                      '&.Mui-focused': {
                        borderRadius: '0 10px 10px 0 !important',
                      },
                    },
                  }}
                  onChange={(event) => {
                    event.preventDefault();
                    field.onChange(event.target.value);
                  }}
                >
                  {Object.values(COMMITMENT_TYPES).map((currentCommitmentType) => (
                    <MenuItem
                      key={currentCommitmentType}
                      value={currentCommitmentType}
                    >
                      {currentCommitmentType}
                    </MenuItem>
                  ))}
                </VayuTextField>
              )}
            />
          </div>

          <Controller
            control={control}
            name={`usageProducts.${productIndex}.isPeriodicConsumption`}
            render={({ field: { value, ...field } }) => (
              <FormControlLabel
                control={(
                  <Checkbox
                    {...field}
                    checked={value}
                  />
                )}
                label="Monthly recurrence"
              />
            )}
          />
        </div>
      </ChargeEditDrawerContentItemOptional>

      <ChargeEditDrawerContentItemOptional
        fieldPath={registerBaseAmount.name}
        label="Add base amount"
        hint="Set a baseline fee to charge regardless of customer usage."
      >
        <Controller
          control={control}
          name={registerBaseAmount.name}
          render={({ field }) => (
            <VayuTextField
              fullWidth
              focused
              placeholder="Base amount"
              multiline
              error={isNotNullOrUndefined(get(errors, registerBaseAmount.name))}
              type="number"
              defaultValue={field.value}
              onChange={handleNumberChange(registerBaseAmount.name)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="end">
                    {`${getCurrencySymbol(currency)} `}
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
      </ChargeEditDrawerContentItemOptional>
    </Stack>
  );
};
